import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/constants";

export default function CartDrawer() {
    const { open, setOpen, items, updateQty, removeItem, subtotal } = useCart();

    React.useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [setOpen]);
    const navigate = useNavigate();

    if (!open) return null;

    const goCheckout = () => {
        setOpen(false);
        navigate("/checkout");
    };

    return (
        <div className="fixed inset-0 z-50" data-testid="cart-drawer">
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => setOpen(false)}
            />
            <aside className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-bg1 border-l border-white/10 flex flex-col">
                <header className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-lime" />
                        <h3 className="font-display text-xl uppercase tracking-tight">Your Cart</h3>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        data-testid="cart-drawer-close"
                        className="p-2 rounded-full hover:bg-white/10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {items.length === 0 && (
                        <div className="text-center py-16">
                            <div className="text-zinc-400 mb-6">Your cart is empty.</div>
                            <Link
                                to="/shop"
                                onClick={() => setOpen(false)}
                                className="btn-pill btn-primary"
                                data-testid="cart-drawer-shop-cta"
                            >
                                Shop ReDrive Max
                            </Link>
                        </div>
                    )}

                    {items.map(({ product, quantity }) => (
                        <div key={product.id} className="flex gap-4 p-3 rounded-sm bg-bg2 border border-white/5">
                            <div className="w-20 h-20 rounded-sm bg-white flex items-center justify-center overflow-hidden">
                                {product.images?.[0] ? (
                                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-bg0 font-display text-sm">{product.name.slice(0, 2)}</div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm">{product.name}</div>
                                <div className="text-xs text-zinc-500">{product.flavor}</div>
                                <div className="mt-2 flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 bg-bg0 rounded-none border border-white/10 px-2 py-1">
                                        <button
                                            data-testid={`cart-qty-decrease-${product.slug}`}
                                            onClick={() => updateQty(product.id, quantity - 1)}
                                            className="p-1 hover:text-lime">
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="text-sm w-5 text-center" data-testid={`cart-qty-${product.slug}`}>{quantity}</span>
                                        <button
                                            data-testid={`cart-qty-increase-${product.slug}`}
                                            onClick={() => updateQty(product.id, quantity + 1)}
                                            className="p-1 hover:text-lime">
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <div className="text-sm font-bold">{formatINR(product.price * quantity)}</div>
                                </div>
                            </div>
                            <button
                                data-testid={`cart-remove-${product.slug}`}
                                onClick={() => removeItem(product.id)}
                                className="text-zinc-500 hover:text-red-400 self-start"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>

                {items.length > 0 && (
                    <footer className="p-5 border-t border-white/10 space-y-3">
                        <div className="flex justify-between text-sm text-zinc-400">
                            <span>Subtotal</span>
                            <span data-testid="cart-subtotal" className="text-white font-bold">{formatINR(subtotal)}</span>
                        </div>
                        <div className="text-xs text-zinc-500">Free shipping over ₹999. Taxes calculated at checkout.</div>
                        <button
                            onClick={goCheckout}
                            data-testid="cart-drawer-checkout-button"
                            className="btn-pill btn-primary w-full"
                        >
                            Proceed to Checkout
                        </button>
                    </footer>
                )}
            </aside>
        </div>
    );
}
